"use client";
import SectionHeader from "@/components/SectionHeader";
import { Images } from "@assets/assets";
import React, {
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import IndustryProcessCard from "./industryprocesscard";
// PERF: GSAP + ScrollTrigger + ScrollToPlugin loaded lazily via
// ensureGsapWithScrollTo() (out of the initial bundle; registerPlugin after
// mount). The gsap instance is cached in gsapRef for the sync goToStep handler.
import type GsapNS from "gsap";
import { ensureGsapWithScrollTo } from "@/lib/EnsureGsap";

import type { StaticImageData } from "next/image";
import Image from "next/image";

type ProcessItem = {
  title: string;
  description: string;
  icon?: string | StaticImageData;
};

type IndustryProcessProps = {
  title?: React.ReactNode;
  subtitle?: string | undefined;
  paragraph?: string;
  items?: ProcessItem[];
  mode?: "light" | "dark";
  className?: string;
  cta?: boolean;
};

export default function IndustryProcess({
  title,
  subtitle,
  paragraph,
  items = [],
  mode = "dark",
  className = "",
  cta = true,
}: IndustryProcessProps) {
  const isLight = mode === "light";
  const sectionRef = useRef<HTMLElement>(null);
  const horizontalTrackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const scrollTriggerInstance = useRef<globalThis.ScrollTrigger | null>(null);
  const gsapRef = useRef<typeof GsapNS | null>(null);

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let cancelled = false;

    ensureGsapWithScrollTo().then(({ ScrollTrigger }) => {
      if (cancelled) return;
      resizeObserver = new ResizeObserver(() => {
        ScrollTrigger.refresh();
      });
      resizeObserver.observe(document.body);
    });

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    let cleanup: (() => void) | null = null;
    let cancelled = false;

    ensureGsapWithScrollTo().then(({ gsap, ScrollTrigger }) => {
      if (cancelled) return;
      gsapRef.current = gsap;

      const ctx = gsap.context(() => {
        const mm = gsap.matchMedia();

        // MOBILE & TABLET: Vertical Stack
        mm.add("(max-width: 1365px)", () => {
          // Reset any horizontal transforms if window is resized from desktop to mobile
          gsap.set(horizontalTrackRef.current, { x: 0 });
          gsap.set(".card-wrapper", { opacity: 1 });
        });

        // DESKTOP: Horizontal Pinning logic
        mm.add("(min-width: 1366px)", () => {
          const track = horizontalTrackRef.current;
          const cards = gsap.utils.toArray<HTMLElement>(".card-wrapper");
          if (!track || cards.length === 0) return;

          // Recompute on every refresh (resize / late layout) instead of capturing
          // once — a captured const makes the function-based end/x return a stale
          // value and defeats invalidateOnRefresh.
          const getDistance = () => track.scrollWidth - window.innerWidth;

          // Timeline units: cards MOVE, then HOLD parked at the end. The trailing
          // hold keeps the section pinned a beat longer so the scrubbed track can
          // finish its last movement BEFORE we unpin. Without it, the pin releases
          // exactly at `end` while scrub is still catching up, so the next section
          // scrolls in while the last cards are still sliding.
          const MOVE = 2;
          const HOLD = 0.2;

          // 1. Initialize as a TIMELINE, not a tween
          const mainTimeline = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              pin: true,
              // `true` (not a number) ties the track position DIRECTLY to scroll
              // with no time-based lag. With `scrub: 1` the animation trailed the
              // scroll by ~1s, so a fast fling unpinned the section at ~80% while
              // the last cards were still sliding — the next section appeared
              // before the horizontal scroll finished. Direct scrub guarantees the
              // track is at 100% exactly when the pin releases.
              scrub: true,
              start: "top top",
              // Scale the scroll distance by the hold so the movement stays ~1:1
              // with scroll and the extra HOLD/MOVE portion is the release buffer.
              end: () => `+=${getDistance() * ((MOVE + HOLD) / MOVE)}`,
              invalidateOnRefresh: true,
              onUpdate: (self) => {
                scrollTriggerInstance.current = self;
              },
            },
          });

          // 2. The Movement
          mainTimeline.to(track, {
            x: () => -getDistance(),
            ease: "none",
            duration: MOVE, // Relative duration in the timeline
          });

          // 3. The Hold: keep the track parked at the end for a beat so the scrub
          //    catches up and the unpin / handoff to the next section feels clean.
          mainTimeline.to({}, { duration: HOLD });

          cards.forEach((card, i) => {
            ScrollTrigger.create({
              trigger: card,
              containerAnimation: mainTimeline,
              start: "left center",
              end: "right+=200 center",
              onUpdate: (self) => {
                if (self.isActive) {
                  setActiveIndex(i);
                }
              },
              onToggle: (self) => {
                gsap.to(card, {
                  opacity: self.isActive ? 1 : 0.3,
                  duration: 0.3,
                  overwrite: true,
                });
              },
            });
          });
        });
      }, sectionRef);

      cleanup = () => ctx.revert();
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [items.length]);

  const goToStep = useCallback(
    (direction: "prev" | "next") => {
      const gsap = gsapRef.current;
      const st = scrollTriggerInstance.current;
      if (!gsap || !st) return;

      const nextIndex =
        direction === "next"
          ? Math.min(activeIndex + 1, items.length - 1)
          : Math.max(activeIndex - 1, 0);

      if (nextIndex === activeIndex) return;

      const totalScroll = st.end - st.start;
      const targetProgress = nextIndex / (items.length - 1);
      const scrollToPoint = st.start + totalScroll * targetProgress;

      gsap.to(window, {
        scrollTo: scrollToPoint,
        duration: 0.8,
        ease: "power2.inOut",
      });
    },
    [activeIndex, items.length],
  );

  return (
    // Wrapper is React-owned; the pin target is the inner section so GSAP's
    // injected pin-spacer never displaces this component's root in the page
    // container's child list. See components/common/Solution/IndustryProcess.tsx.
    <div className={`w-full ${className}`}>
      <section
        ref={sectionRef}
        className={`industry-process relative overflow-hidden flex flex-col justify-center min-h-screen ${
          isLight ? "bg-white" : "bg-black"
        } lg:pb-[150px] pb-[60px]`}
      >
        {!isLight && (
          <>
            <div className="texture-bg mix-blend-overlay  w-full h-full absolute top-0 left-0 z-0">
              <div className="absolute inset-0 z-0">
                <Image
                  src={Images?.texture}
                  alt="texture"
                  fill
                  className="object-cover mix-blend-overlay opacity-60"
                  priority
                  sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
                />
              </div>
            </div>
            <div className="w-[907px] h-[907px] bg-[#225bd4]/20 rounded-full blur-[333px] absolute right-0 top-0 translate-x-1/2 -translate-y-1/2" />
            <div className="w-[907px] h-[907px] bg-[#225bd4]/20 rounded-full blur-[333px] absolute left-0 bottom-0 -translate-x-1/2 translate-y-1/2" />
          </>
        )}

        <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)] relative z-10 pt-[50px] lg:pt-[75px]">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <SectionHeader
              title={title}
              subtitle={subtitle}
              paragraph={paragraph}
              dark={!isLight}
            />

            {/* Navigation Buttons (Desktop Only) */}
            <div className="hidden [@media(min-width:1366px)]:flex items-start gap-3 mb-10">
              <button
                onClick={() => goToStep("prev")}
                disabled={activeIndex === 0}
                className={`p-2 rounded-full border transition-all duration-300 flex items-center justify-center enabled:cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed ${
                  isLight
                    ? "border-black/20 enabled:hover:bg-black/10"
                    : "border-white/20 enabled:hover:bg-white/10"
                }`}
              >
                <div
                  className={`w-6 h-6 flex items-center justify-center ${isLight ? "text-black" : "text-white"}`}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </div>
              </button>

              <button
                onClick={() => goToStep("next")}
                disabled={activeIndex === items.length - 1}
                className="p-2 rounded-full border border-transparent transition-all duration-300 flex items-center justify-center enabled:cursor-pointer disabled:cursor-not-allowed"
                style={{
                  backgroundColor:
                    activeIndex === items.length - 1 ? "#225BD44D" : "#225BD4",
                }}
              >
                <div className="w-6 h-6 flex items-center justify-center text-white">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-0 sm:mt-10 relative">
          {/* Background Grid (Desktop Only) */}
          <div className="absolute inset-0 w-full h-full pointer-events-none z-0 hidden [@media(min-width:1366px)]:block">
            <div
              className={`absolute top-[15px] left-0 right-0 h-px ${isLight ? "bg-black" : "bg-white"} opacity-10`}
            />
            <div className="grid grid-cols-33 h-full w-full px-[100px]">
              {Array.from({ length: 33 })?.map((_, index) => (
                <div
                  key={index}
                  className="flex justify-start border-l border-white/10 h-full"
                >
                  <div
                    className={`w-px ${isLight ? "bg-black" : "bg-white"} opacity-10 h-[30px] mt-[0px]`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div
            ref={horizontalTrackRef}
            className={`
              mt-[17px] relative z-10 flex
              /* Mobile/Tablet: Stacked Column */
              flex-col items-center gap-y-0 lg:gap-y-8 px-[5%]
              /* Desktop: Horizontal Track */
              [@media(min-width:1366px)]:flex-row [@media(min-width:1366px)]:items-start [@media(min-width:1366px)]:flex-nowrap [@media(min-width:1366px)]:px-[25vw] [@media(min-width:1366px)]:gap-x-20
            `}
          >
            {items?.map((item, idx) => (
              <div
                key={idx}
                className="card-wrapper flex-shrink-0 w-full md:w-[600px] [@media(min-width:1366px)]:opacity-30 transition-opacity duration-300"
              >
                <IndustryProcessCard
                  cta={cta}
                  mode={isLight ? "light" : "dark"}
                  title={item.title}
                  description={item.description}
                  icon={item.icon}
                  isActive={activeIndex === idx} // Always active/highlighted on mobile
                />
              </div>
            ))}

            {/* Spacer (Desktop Only) */}
            <div className="hidden [@media(min-width:1366px)]:block flex-shrink-0 w-[600px] pointer-events-none">
              <div className="w-full h-full min-h-[300px]" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
